import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  Directive,
  ElementRef,
  OnInit,
  NgZone,
  inject,
  DestroyRef,
  input,
} from '@angular/core';
import { fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Directive that synchronizes horizontal scrolling between two elements.
 * When the target element (specified by scrollWatch) is scrolled horizontally,
 * the source element's scroll position is updated to match.
 */
@Directive({ selector: '[tableScrollSync]' })
export class Ag1TableScrollSyncDirective implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private el = inject(ElementRef);
  private ngZone = inject(NgZone);
  /**
   * The virtual scroll viewport to watch for scroll events.
   * This element's horizontal scroll position will be synchronized with the source element.
   */
  readonly scrollWatch = input<CdkVirtualScrollViewport>(undefined);

  /** The element that will mirror the scroll position */
  private sourceElement: HTMLElement;
  /** The element being watched for scroll events */
  private targetElement: HTMLElement;
  /** Tracks the last known scroll position to prevent unnecessary updates */
  private lastKnownScrollLeft: number = 0;
  /** Stores the current animation frame ID for cleanup */
  private frameId: number | null = null;

  constructor() {}

  ngOnInit(): void {
    if (!this.scrollWatch()) {
      console.error('TableScrollSync: scrollWatch is required');
      return;
    }

    this.initializeElements();
    this.setupScrollSync();
  }

  private initializeElements(): void {
    this.sourceElement = this.el.nativeElement;
    this.targetElement = this.scrollWatch().elementRef.nativeElement;
  }

  private setupScrollSync(): void {
    // Performance optimisation: Avoid angular change detection
    this.ngZone.runOutsideAngular(() => {
      // Performance optimisation: use passive event listener to avoid blocking the main thread
      // https://stackoverflow.com/a/78265405
      fromEvent(this.targetElement, 'scroll', { passive: true })
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(() => {
          const currentScrollLeft: number = this.targetElement.scrollLeft;

          // If there's no change in scroll position, don't proceed
          if (currentScrollLeft === this.lastKnownScrollLeft) {
            return;
          }

          // Performance optimisation: Use requestAnimationFrame to avoid layout thrashing and repaints
          this.frameId = requestAnimationFrame(() => {
            this.sourceElement.scrollLeft = currentScrollLeft;
            this.lastKnownScrollLeft = currentScrollLeft;
            this.frameId = null;
          });
        });
    });
  }

  /**
   * Hack to force a recalculation of column widths.
   * This is necessary because the column widths are calculated based on the
   * scroll position of the target element, and the scroll position is not
   * updated when the columns are dropped.
   */
  public forceSync(): void {
    if (!this.targetElement || this.frameId !== null) {
      return; // Prevent multiple simultaneous calls
    }

    this.ngZone.runOutsideAngular(() => {
      const originalScrollLeft: number = this.targetElement.scrollLeft;

      // Choose nudge direction based on current position
      const nudgeAmount: number = originalScrollLeft > 0 ? -1 : 1;

      // Apply nudge
      this.targetElement.scrollLeft = originalScrollLeft + nudgeAmount;

      // Restore original position on next frame
      this.frameId = requestAnimationFrame(() => {
        this.targetElement.scrollLeft = originalScrollLeft;
        this.frameId = null;
      });
    });
  }
}
